import { statusExplanations } from '../config/statusExplanations';
import React from 'react';
import './ProjectDetail.css';
import { Link, useLocation } from 'react-router-dom';
import Header from './Header';
import StreetView from './StreetView';
import ProjectStatusChevron from './ProjectStatusChevron';

function ProjectDetail({ name, projectName, userInfo, groundUp, rollout }) {
  const location = useLocation();
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // If location.state is missing, try to load from localStorage
  let projectState = location.state;
  if (!projectState) {
    try {
      const stored = localStorage.getItem('selectedProject');
      if (stored) {
        projectState = JSON.parse(stored);
      }
    } catch (e) {
      projectState = undefined;
    }
  }

  // Prefer props, fallback to projectState
  const displayName = name || (projectState && projectState.Name);
  const displayProjectName = projectName || (projectState && projectState.Project_Name__c);
  const displayGroundUp = groundUp || (projectState && projectState.Ground_Up__c);
  const displayRollout = rollout || (projectState && projectState.Rollout__c);

  const formatDateMMDDYYYY = (value) => {
    if (!value) return 'N/A';

    const parsedDate = parseDateValue(value);
    if (!parsedDate) return 'N/A';

    const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
    const day = String(parsedDate.getDate()).padStart(2, '0');
    const year = parsedDate.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const parseDateValue = (value) => {
    if (!value) return null;

    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : new Date(value.getTime());
    }

    if (typeof value === 'string') {
      const trimmedValue = value.trim();

      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmedValue)) {
        const [year, month, day] = trimmedValue.split('-').map(Number);
        return new Date(year, month - 1, day);
      }

      const slashDateMatch = trimmedValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (slashDateMatch) {
        const [, month, day, year] = slashDateMatch;
        return new Date(Number(year), Number(month) - 1, Number(day));
      }
    }

    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  };

  const isPastDate = (value) => {
    const parsedDate = parseDateValue(value);
    if (!parsedDate) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    parsedDate.setHours(0, 0, 0, 0);

    return parsedDate < today;
  };

  const normalizeStatusValue = (value) => String(value || '').trim().toLowerCase();
  const billingState = normalizeStatusValue(projectState && projectState.Project_State__c);
  const isProjectBillingCompleted = billingState === 'complete' || billingState === 'completed';

  const isTerminalServiceStatus = (value) => {
    const normalized = normalizeStatusValue(value);
    return normalized === 'complete'
      || normalized === 'completed'
      || normalized === 'services fulfilled'
      || normalized === 'fulfilled';
  };

  const isServiceCompleted = (service) => {
    if (!service) return false;

    const billingStatus = normalizeStatusValue(service.Status__c);
    if (billingStatus) {
      return isTerminalServiceStatus(billingStatus);
    }

    return isTerminalServiceStatus(service.Application_Status__c);
  };

  // Services table sorting and filtering
  const servicesColumns = [
    { key: 'Phase', label: 'Phase' },
    { key: 'Service_Name_Full__c', label: 'Services' },
    { key: 'Status__c', label: 'Billing Status' },
    { key: 'Application_Status__c', label: 'Application Status' },
    { key: 'Next_Action_With__c', label: 'Next Action With' },
    { key: 'Expected_End_Date__c', label: 'Expected Completion Date' },
    { key: 'Client_Notes__c', label: 'Notes' },
  ];
  const [servicesSortConfig, setServicesSortConfig] = React.useState({ key: 'Expected_End_Date__c', direction: 'asc' });
  const [servicesFilters, setServicesFilters] = React.useState(() => Object.fromEntries(servicesColumns.map(col => [col.key, ''])));
  // Get unique phase values for dropdown
  const phaseOptions = React.useMemo(() => {
    if (!projectState || !Array.isArray(projectState.projectServices)) return [];
    const allPhases = projectState.projectServices.map(s => s.Phase__r?.Name || 'N/A');
    return Array.from(new Set(allPhases)).filter(Boolean);
  }, [projectState]);
  const handleServicesSort = (colKey) => {
    setServicesSortConfig(prev => {
      if (prev.key === colKey) {
        return { key: colKey, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key: colKey, direction: 'asc' };
    });
  };
  const handleServicesFilterChange = (colKey, value) => {
    setServicesFilters(f => ({ ...f, [colKey]: value }));
  };
  const filteredSortedServices = React.useMemo(() => {
    if (!projectState || !Array.isArray(projectState.projectServices)) return [];
    const flat = projectState.projectServices.map(service => ({
      ...service,
      Phase: service.Phase__r?.Name || 'N/A',
      Expected_End_Date__c: parseDateValue(service.Expected_End_Date__c),
    }));
    // Filtering
    const filtered = flat.filter(service => {
      return servicesColumns.every(col => {
        let val = service[col.key];
        if (col.key === 'Expected_End_Date__c' && val instanceof Date) {
          val = val.toLocaleDateString();
        }
        val = val || '';
        return val.toString().toLowerCase().includes((servicesFilters[col.key] || '').toLowerCase());
      });
    });
    // Sorting
    const sorted = [...filtered];
    sorted.sort((a, b) => {
      let aVal = a[servicesSortConfig.key];
      let bVal = b[servicesSortConfig.key];
      if (servicesSortConfig.key === 'Expected_End_Date__c') {
        aVal = aVal ? aVal.getTime() : 0;
        bVal = bVal ? bVal.getTime() : 0;
      }
      if (aVal === undefined || aVal === null) aVal = '';
      if (bVal === undefined || bVal === null) bVal = '';
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return servicesSortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      return servicesSortConfig.direction === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
    return sorted;
  }, [projectState, servicesSortConfig, servicesFilters]);

  if (!projectState) {
    return (
      <>
        <Header userInfo={userInfo} />
        <div className="project-detail-header">
          <h1>Project Details</h1>
          <Link to="/" className="btn btn-secondary" style={{ marginBottom: 24 }}>Back to Dashboard</Link>
        </div>
        <div className="card-row">
          <div className="card">
            <div className="card-header">
              <h2>Project Unavailable</h2>
            </div>
            <div className="card-body">
              <p>This project could not be loaded from the current page state. Return to the dashboard and reopen the project.</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header userInfo={userInfo} />
      <div className="project-detail-header">
        <h1>Project Details</h1>
        <Link to="/" className="btn btn-secondary" style={{ marginBottom: 24 }}>Back to Dashboard</Link>
      </div>
      <div className="chevron-above-cards" style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '75vw', minWidth: 0, margin: '0 auto', gap: 16, marginBottom: '16px' }}>
          {/* Project State label and image */}
          <div className="project-detail-summary" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontWeight: 'bold', color: '#0a2740', fontSize: '1.2em', marginRight: 2 }}>Billing State:</span>
            {(() => {
              let state = projectState && projectState.Project_State__c;
              // Use shared statusExplanations
              let label = state;
              let style = {};
              if (state === 'Project Hold') {
                style = { background: '#FFE600', color: '#222', fontWeight: 'bold', borderRadius: '12px', padding: '8px 24px', fontSize: '1.1em', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 120, marginLeft: 14, cursor: 'pointer' };
              } else if (state === 'Accounting Hold - Past Due Unpaid Invoices' || state === 'Hold - Past Due Invoices') {
                style = { background: '#FF3B3B', color: '#fff', fontWeight: 'bold', borderRadius: '12px', padding: '8px 24px', fontSize: '1.1em', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 120, marginLeft: 14, cursor: 'pointer' };
                label = 'Accounting Hold';
              } else if (state === 'Cancelled') {
                style = { background: '#FFD600', color: '#222', fontWeight: 'bold', borderRadius: '12px', padding: '8px 24px', fontSize: '1.1em', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 120, marginLeft: 14, cursor: 'pointer' };
              } else if (state === 'Complete') {
                style = {
                  background: '#0a2740',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '0.98em',
                  borderRadius: '8px',
                  height: '52px',
                  minWidth: '120px',
                  maxWidth: '180px',
                  width: '100%',
                  margin: '0 auto',
                  letterSpacing: '0.02em',
                  textTransform: 'uppercase',
                  textAlign: 'center',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginLeft: 14,
                  cursor: 'pointer'
                };
                label = 'Complete';
              } else {
                style = { background: '#fff', color: '#0a2740', fontWeight: 'bold', borderRadius: '12px', padding: '8px 24px', fontSize: '1.1em', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 120, border: '2px solid #0a2740', marginLeft: 10, cursor: 'pointer' };
                label = 'In Progress';
              }
              const tooltipText = statusExplanations[state] || statusExplanations[label] || '';
              return (
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <span
                    className={`portal-status-badge billing-badge ${label === 'Accounting Hold' ? 'is-red' : label === 'Project Hold' || label === 'Cancelled' ? 'is-amber' : label === 'Complete' ? 'is-neutral' : 'is-green'}`}
                    style={style}
                    onMouseEnter={e => {
                      const tooltip = e.currentTarget.nextSibling;
                      if (tooltip) tooltip.style.opacity = 1;
                    }}
                    onMouseLeave={e => {
                      const tooltip = e.currentTarget.nextSibling;
                      if (tooltip) tooltip.style.opacity = 0;
                    }}
                  >
                    {label}
                  </span>
                  <span
                    style={{
                      position: 'absolute',
                      left: '50%',
                      top: '110%',
                      transform: 'translateX(-50%)',
                      background: '#0a2740',
                      color: '#fff',
                      padding: '8px 14px',
                      borderRadius: '8px',
                      fontSize: '0.95em',
                      fontWeight: 400,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                      whiteSpace: 'pre-line',
                      zIndex: 10,
                      opacity: 0,
                      pointerEvents: 'none',
                      transition: 'opacity 0.2s',
                      minWidth: '220px',
                      maxWidth: '320px',
                    }}
                  >
                    {tooltipText}
                  </span>
                </div>
              );
            })()}
            <div style={{ display: 'flex', alignItems: 'center', marginLeft: 32 }}>
              <span style={{ fontWeight: 'bold', color: '#0a2740', fontSize: '1.2em', marginRight: 8 }}>Expected End Date:</span>
                <span
                  className={!isProjectBillingCompleted && isPastDate(projectState && projectState.Project_Expected_End_Date__c) ? 'expected-end-date past-due' : 'expected-end-date'}
                >
                {formatDateMMDDYYYY(projectState && projectState.Project_Expected_End_Date__c)}
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%', minWidth: 0 }}>
            <span style={{ fontWeight: 'bold', color: '#0a2740', fontSize: '1.2em', marginRight: 2 }}>Project Status:</span>
            <ProjectStatusChevron status={projectState.Project_Status__c} />
          </div>
        </div>
      </div>
      <div className="card-row project-summary-row" style={{ marginBottom: -30}}>
        <div className="card">
          <div className="card-header">
            <h2>Overview</h2>
          </div>
          <div className="card-body">
            {projectState && (
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
                <StreetView
                  address={
                    projectState.Building_Formula__c +
                    (projectState.Floor_s__c ? ', ' + projectState.Floor_s__c : '') +
                    ', ' + projectState.Building_City__c +
                    ', ' + projectState.Building_State__c +
                    ' ' + projectState.Building_ZIP__c
                  }
                  width={400}
                  height={200}
                />
              </div>
            )}
            <p><strong>Site address full:</strong> {projectState && (projectState.Building_Formula__c + ((', ' + projectState.Floor_s__c) || '') + ', ' + projectState.Building_City__c + ', ' + projectState.Building_State__c + ' ' + projectState.Building_ZIP__c)}</p>
            <p><strong>Client Name:</strong> {(projectState.Billing_Company_Name__r && projectState.Billing_Company_Name__r.Name) || 'N/A'}</p>
            <p><strong>Client Project Name:</strong> {displayProjectName || 'N/A'}</p>
            <p><strong>Client Project Number:</strong> {displayName || 'N/A'}</p>
            <p><strong>Ground Up:</strong> {displayGroundUp || 'N/A'}</p>
            <p><strong>Rollout:</strong> {displayRollout || 'N/A'}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-header">
            <h2>Roster</h2>
          </div>
          <div className="card-body">
            <p><strong>Milrose PM:</strong></p>
            <p>{((projectState && projectState.milrosePM) || 'N/A') + ', ' + ((projectState && projectState.milrosePMEmail) || 'N/A') + ', ' + ((projectState && projectState.milrosePMPhone) || 'N/A')}</p>
          <p><strong>Client PM:</strong></p>
          {projectState && Array.isArray(projectState.projectRoster) && projectState.projectRoster.filter(rosterItem => rosterItem.Role__c === 'Client Project Manager').length > 0 ? (
            <ul>
              {projectState.projectRoster.filter(rosterItem => rosterItem.Role__c === 'Client Project Manager').slice(0, 2).map((pm, idx) => (
                <li key={idx}>
                  {pm.Name_Full__c || 'N/A'}{pm.Email__c ? `, ${pm.Email__c}` : ''}{pm.Phone__c ? `, ${pm.Phone__c}` : ''}
                </li>
              ))}
            </ul>
          ) : (
            <p>N/A</p>
          )}
          <p><strong>Regional Lead:</strong></p>
          {projectState && Array.isArray(projectState.projectRoster) && projectState.projectRoster.filter(rosterItem => rosterItem.Role__c === 'Regional Lead').length > 0 ? (
            <ul>
              {projectState.projectRoster.filter(rosterItem => rosterItem.Role__c === 'Regional Lead').slice(0, 2).map((pm, idx) => (
                <li key={idx}>
                  {pm.Name_Full__c || 'N/A'}{pm.Email__c ? `, ${pm.Email__c}` : ''}{pm.Phone__c ? `, ${pm.Phone__c}` : ''}
                </li>
              ))}
            </ul>
          ) : (
            <p>N/A</p>
          )}
          <p><strong>Team Lead:</strong></p>
          {projectState && Array.isArray(projectState.projectRoster) && projectState.projectRoster.filter(rosterItem => rosterItem.Role__c === 'Team Lead').length > 0 ? (
            <ul>
              {projectState.projectRoster.filter(rosterItem => rosterItem.Role__c === 'Team Lead').slice(0, 2).map((pm, idx) => (
                <li key={idx}>
                  {pm.Name_Full__c || 'N/A'}{pm.Email__c ? `, ${pm.Email__c}` : ''}{pm.Phone__c ? `, ${pm.Phone__c}` : ''}
                </li>
              ))}
            </ul>
          ) : (
            <p>N/A</p>
          )}
          <p><strong>GC:</strong></p>
          {projectState && Array.isArray(projectState.projectRoster) && projectState.projectRoster.filter(rosterItem => rosterItem.Role__c.startsWith('General Contractor')).length > 0 ? (
            <ul>
              {projectState.projectRoster.filter(rosterItem => rosterItem.Role__c.startsWith('General Contractor')).map((pm, idx) => (
                <li key={idx}>
                  {pm.Name_Full__c || 'N/A'}{pm.Email__c ? `, ${pm.Email__c}` : ''}{pm.Phone__c ? `, ${pm.Phone__c}` : ''}
                </li>
              ))}
            </ul>
          ) : (
            <p>N/A</p>
          )}
          <p><strong>Architect:</strong></p>
          {projectState && Array.isArray(projectState.projectRoster) && projectState.projectRoster.filter(rosterItem => rosterItem.Role__c.startsWith('Architect')).length > 0 ? (
            <ul>
              {projectState.projectRoster.filter(rosterItem => rosterItem.Role__c.startsWith('Architect')).map((pm, idx) => (
                <li key={idx}>
                  {pm.Name_Full__c || 'N/A'}{pm.Email__c ? `, ${pm.Email__c}` : ''}{pm.Phone__c ? `, ${pm.Phone__c}` : ''}
                </li>
              ))}
            </ul>
          ) : (
            <p>N/A</p>
          )}
          <p><strong>A/R Coordinator:</strong> </p>
          <p>{
            (!projectState || (!projectState.arCoordinatorName && !projectState.arCoordinatorEmail && !projectState.arCoordinatorPhone))
              ? 'N/A'
              : [
                  projectState.arCoordinatorName || 'N/A',
                  projectState.arCoordinatorEmail || 'N/A',
                  projectState.arCoordinatorPhone || 'N/A'
                ].join(', ')
          }</p>
        </div>
      </div>
    </div>
      <div className="card card-wide">
        <div className="card-header">
          <h2>Project Status Update</h2>
        </div>
        <div className="card-body">
          <pre style={{whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0}}>{(projectState && projectState.notes) || 'N/A'}</pre>
        </div>
      </div>
      <div className="card card-wide">
        <div className="card-header">
          <h2>Services Data</h2>
        </div>
        <div className="card-body services-panel-body">
          <table className="table table-striped">
            <thead>
              <tr>
                {servicesColumns.map(col => (
                  <th key={col.key} style={{cursor:'pointer'}} onClick={() => handleServicesSort(col.key)}>
                    {col.label}{servicesSortConfig.key===col.key? (servicesSortConfig.direction==='asc'? ' ▲':' ▼'):''}
                  </th>
                ))}
              </tr>
              <tr>
                {servicesColumns.map(col => (
                  <th key={col.key}>
                    {col.key === 'Phase' ? (
                      <select
                        value={servicesFilters.Phase}
                        onChange={e => handleServicesFilterChange('Phase', e.target.value)}
                        style={{ width: '95%' }}
                      >
                        <option value="">All Phases</option>
                        {phaseOptions.map(phase => (
                          <option key={phase} value={phase}>{phase}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={servicesFilters[col.key]}
                        onChange={e => handleServicesFilterChange(col.key, e.target.value)}
                        placeholder={`Filter ${col.label}`}
                        style={{ width: '95%' }}
                      />
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredSortedServices.map((service, idx) => (
                <tr key={idx}>
                  <td>{service.Phase}</td>
                  <td>{service.Service_Name_Full__c || 'N/A'}</td>
                  <td>{service.Status__c || 'N/A'}</td>
                  <td>{service.Application_Status__c || 'N/A'}</td>
                  <td>{service.Next_Action_With__c || 'N/A'}</td>
                  <td className={service.Expected_End_Date__c && isPastDate(service.Expected_End_Date__c) && !isServiceCompleted(service) ? 'services-expected-date-overdue' : ''}>
                    {formatDateMMDDYYYY(service.Expected_End_Date__c)}
                  </td>
                  <td>{service.Client_Notes__c || 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

export default ProjectDetail;